import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { db } from '../firebase/firebaseConfig';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { customToast } from '../utils/toastUtils';
import './AIImprovementPage.css';
import './AICareerToolkit.css';

const AICareerToolkit = () => {
  const { userData } = useUser();

  const STORAGE_KEY = 'ai_career_toolkit_state';

  const [jobDomain, setJobDomain] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.jobDomain || ''; } catch { return ''; }
  });
  const [jobTitle, setJobTitle] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.jobTitle || ''; } catch { return ''; }
  });
  const [skills, setSkills] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.skills || ''; } catch { return ''; }
  });
  const [experienceLevel, setExperienceLevel] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.experienceLevel || ''; } catch { return ''; }
  });
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.courses || null; } catch { return null; }
  });
  const [availableDomains, setAvailableDomains] = useState([]);
  const [availableLevels, setAvailableLevels] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Persist state to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      jobDomain, jobTitle, skills, experienceLevel, courses
    }));
  }, [jobDomain, jobTitle, skills, experienceLevel, courses]);

  // Fetch unique jobDomain and experienceLevel values from careerToolkitCourses collection
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'careerToolkitCourses'));
        const domains = new Set();
        const levels = new Set();
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (data.jobDomain) domains.add(data.jobDomain);
          if (data.experienceLevel) levels.add(data.experienceLevel);
        });
        setAvailableDomains([...domains].sort());
        // Sort experience levels by a logical order (Entry < Mid < Senior < Lead)
        const levelOrder = ['Entry', 'Mid', 'Senior', 'Lead'];
        setAvailableLevels([...levels].sort((a, b) => {
          const aIndex = levelOrder.findIndex(prefix => a.startsWith(prefix));
          const bIndex = levelOrder.findIndex(prefix => b.startsWith(prefix));
          return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex);
        }));
      } catch (error) {
        console.error('Error fetching dropdown options:', error);
        customToast.error('Failed to load dropdown options');
      } finally {
        setLoadingOptions(false);
      }
    };
    fetchDropdownOptions();
  }, []);

  const handleFindCourses = async () => {
    if (!jobDomain) {
      customToast.error('Please select a job domain');
      return;
    }

    if (!experienceLevel) {
      customToast.error('Please select an experience level');
      return;
    }

    if (!userData?.uid) {
      customToast.error('Please login to use this feature');
      return;
    }

    setLoading(true);
    
    try {
      // Query Firestore for course recommendations
      const coursesRef = collection(db, 'careerToolkitCourses');
      const q = query(
        coursesRef,
        where('jobDomain', '==', jobDomain),
        where('experienceLevel', '==', experienceLevel)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const courseData = querySnapshot.docs[0].data();
        setCourses(courseData.courses || []);
        
        customToast.success('Course recommendations loaded successfully!');
        
        // Scroll to results section
        setTimeout(() => {
          document.getElementById('results-section')?.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }, 500);
      } else {
        customToast.warning('No course recommendations found for this combination. Please try different options.');
        setCourses([]);
      }
    } catch (error) {
      console.error('❌ Error fetching courses:', error);
      customToast.error('Failed to fetch course recommendations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tool-page">
      {/* Hero Section */}
      <div className="toolkit-hero">
        <div className="toolkit-hero-content">
          <div className="hero-badge">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
            </svg>
            <span>AI Career Toolkit</span>
          </div>
          <h1 className="toolkit-title">
            Identify Your Skill Gaps & Accelerate Your Career Growth
          </h1>
          <p className="toolkit-tagline">
            Receive personalized, role-specific course recommendations to stay competitive in today's evolving job market
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="toolkit-content">
        {/* Description Card */}
        <div className="toolkit-description-card">
          <div className="description-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="url(#descGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <defs>
                <linearGradient id="descGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3b82f6"/>
                  <stop offset="100%" stopColor="#8b5cf6"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          <p className="toolkit-description">
            Our Career Toolkit helps you understand exactly what skills you need to grow in your current role or transition 
            into higher opportunities. By analyzing your job title and existing skills, the system identifies missing 
            competencies and recommends carefully selected courses aligned with industry expectations. Instead of 
            random learning, you receive a structured, focused learning plan that strengthens your profile, improves 
            employability, and helps you progress confidently toward your career goals.
          </p>
        </div>

        {/* Input Form Section */}
        <div className="toolkit-form-section">
          <h2 className="section-title">Get Your Personalized Course Recommendations</h2>
          <div className="toolkit-form">
            <div className="form-group">
              <label className="form-label">
                Job Domain <span className="required">*</span>
              </label>
              <select 
                className="form-select"
                value={jobDomain}
                onChange={(e) => setJobDomain(e.target.value)}
                disabled={loadingOptions}
              >
                <option value="">{loadingOptions ? 'Loading domains...' : 'Select your job domain'}</option>
                {availableDomains.map((domain) => (
                  <option key={domain} value={domain}>{domain}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                Job Title <span className="optional">(Optional)</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., Software Engineer, Marketing Manager"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Skills You Already Know <span className="optional">(Optional)</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., Python, React, Project Management (comma-separated)"
                value={skills}
                onChange={(e) => setSkills(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Experience Level <span className="required">*</span>
              </label>
              <select 
                className="form-select"
                value={experienceLevel}
                onChange={(e) => setExperienceLevel(e.target.value)}
                disabled={loadingOptions}
              >
                <option value="">{loadingOptions ? 'Loading levels...' : 'Select your experience level'}</option>
                {availableLevels.map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            <button 
              className="toolkit-cta-button"
              onClick={handleFindCourses}
              disabled={!jobDomain || !experienceLevel || loading}
            >
              {loading ? (
                <>
                  <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/>
                    <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
                  </svg>
                  Loading...
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Get Course Recommendations
                </>
              )}
            </button>
          </div>
        </div>

        {/* Show How It Works and What You Get only when no results */}
        {!courses && (
          <>
            {/* How It Works Section */}
            <div className="how-it-works-section">
          <h2 className="section-title">How It Works</h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>Choose Your Domain</h3>
              <p>Select your job domain from the dropdown</p>
            </div>

            <div className="step-card">
              <div className="step-number">2</div>
              <h3>Select Experience Level</h3>
              <p>Choose your current experience range</p>
            </div>

            <div className="step-card">
              <div className="step-number">3</div>
              <h3>Get Recommendations</h3>
              <p>Click the button to fetch tailored course links</p>
            </div>

            <div className="step-card">
              <div className="step-number">4</div>
              <h3>View Courses</h3>
              <p>Browse recommended courses from top platforms</p>
            </div>

            <div className="step-card">
              <div className="step-number">5</div>
              <h3>Start Learning</h3>
              <p>Click on course links to begin upskilling</p>
            </div>

            <div className="step-card">
              <div className="step-number">6</div>
              <h3>Track Progress</h3>
              <p>Come back anytime to explore more courses</p>
            </div>
          </div>
        </div>

        {/* What You Get Section */}
        <div className="benefits-section">
          <h2 className="section-title">What You Get</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon purple">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Curated Course Links</h3>
              <p>Handpicked courses from Udemy, Coursera, and other top platforms.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon blue">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Role-Specific Recommendations</h3>
              <p>Courses tailored to your job domain and experience level.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon green">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Industry-Relevant Content</h3>
              <p>Learn skills that matter in today's job market.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon orange">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Time-Saving</h3>
              <p>No more endless searching - get direct access to quality courses.</p>
            </div>
          </div>
        </div>
          </>
        )}

        {/* Results Section */}
        {courses && (
          <div id="results-section" className="results-section">
            <div className="results-header">
              <h2 className="section-title">📚 Recommended Courses for You</h2>
              <p className="results-subtitle">
                Based on your {jobDomain.replace('-', ' ')} domain and {experienceLevel} experience level
              </p>
            </div>
            
            {courses.length === 0 ? (
              <div className="no-courses-message">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                  <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h3>No courses found</h3>
                <p>We don't have course recommendations for this combination yet. Please try different options.</p>
              </div>
            ) : (
              <div className="courses-container">
                <div className="courses-list">
                  {courses.map((course, index) => (
                    <div key={index} className="course-card-horizontal">
                      <div className="course-content-section">
                        <div className="course-number">Course {index + 1}</div>
                        <h3 className="course-title-enhanced">{course.name || course.title}</h3>
                        
                        {course.description ? (
                          <p className="course-description">{course.description}</p>
                        ) : (
                          <p className="course-description">
                            Develop expertise in <strong>{course.name || course.title}</strong> with curated courses from top learning platforms tailored to your experience level.
                          </p>
                        )}
                        
                        {course.skills && course.skills.length > 0 && (
                          <div className="course-skills">
                            <strong>Skills You'll Learn:</strong>
                            <div className="skills-tags">
                              {course.skills.map((skill, idx) => (
                                <span key={idx}>{skill}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="course-providers-horizontal">
                        <strong className="providers-heading">Choose Your Platform:</strong>
                        <div className="provider-buttons-row">
                          {course.providers && course.providers.map((provider, idx) => (
                            <div key={idx} className="provider-card">
                              <div className="provider-icon-large">
                                {(provider.provider || provider.name) === 'LinkedIn Learning' && (
                                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                    <rect width="24" height="24" rx="4" fill="#0A66C2"/>
                                    <path d="M7.5 8.5h-2v7h2v-7zm-1-3.2a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4zm9.5 3c-1.3 0-2.1.7-2.5 1.4V8.5h-2v7h2v-3.9c0-1 .4-1.6 1.3-1.6.8 0 1.2.6 1.2 1.6v3.9h2v-4.2c0-2.2-1.1-3.3-2-3.3z" fill="white"/>
                                  </svg>
                                )}
                                {(provider.provider || provider.name) === 'Coursera' && (
                                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                    <rect width="24" height="24" rx="4" fill="#0056D2"/>
                                    <path d="M12 6C8.7 6 6 8.7 6 12s2.7 6 6 6 6-2.7 6-6-2.7-6-6-6zm2.4 9.2c-.7.5-1.5.8-2.4.8-2.2 0-4-1.8-4-4s1.8-4 4-4c.9 0 1.7.3 2.4.8l-1.2 1.6c-.3-.3-.7-.4-1.2-.4-1.3 0-2.4 1.1-2.4 2.4 0 1.3 1.1 2.4 2.4 2.4.5 0 .9-.2 1.2-.4l1.2 1.6z" fill="white"/>
                                  </svg>
                                )}
                                {(provider.provider || provider.name) === 'Udemy' && (
                                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                    <rect width="24" height="24" rx="4" fill="#A435F0"/>
                                    <path d="M8 7v2.5l1.2.7V7H8zm2.8 0v4c0 .7.5 1.2 1.2 1.2s1.2-.5 1.2-1.2V7h-1.2v4c0 .1 0 .2-.1.2 0 0-.1.1-.2.1s-.1 0-.2-.1c0 0-.1-.1-.1-.2V7h-.6zm4.2 0v4c0 1.4-1 2.5-2.4 2.5S10.2 12.4 10.2 11V7H9v4c0 2 1.5 3.5 3.5 3.5S16 13 16 11V7h-1z" fill="white"/>
                                  </svg>
                                )}
                              </div>
                              <h4 className="provider-name-large">{provider.provider || provider.name}</h4>
                              {provider.duration && (
                                <div className="provider-info">
                                  <span className="info-badge">⏱️ {provider.duration}</span>
                                </div>
                              )}
                              {provider.level && (
                                <div className="provider-info">
                                  <span className="info-badge">📊 {provider.level}</span>
                                </div>
                              )}
                              <a 
                                href={provider.link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="view-course-button"
                              >
                                View Course
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                  <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AICareerToolkit;
